import { supabase, isConfigured } from './supabase.js';
import { currentUser, isAnonymous } from './auth.js';
import { emit, on } from '../state/bus.js';

/* Whether this session may use Motion — the animation stage, its timeline,
   and video export.

   Same shape as services/entitlements.js, and for the same reason: a
   read-only cache of something the server owns. The licence lives in
   `motion_licences` (0010) behind RLS, so nothing here grants anything; this
   only asks and remembers the answer. Refreshed on 'auth:changed' and
   whenever an admin edits a licence, each of which fires 'motion:changed' so
   the locked surfaces — the stage tab, the timeline dock, the stopwatches,
   the export cards, the watermark — all repaint together rather than
   drifting apart until something unrelated happens to redraw one of them.

   The active/inactive answer comes from my_motion_licence(), which compares
   against now() in Postgres. expires_at is cached alongside it only so the
   UI can say "expires in 24 days"; it is deliberately NOT what decides
   access, because a browser clock is a thing the user can set.

   Fails CLOSED everywhere — no backend, no session, an anonymous session, a
   network error, a malformed reply all read as "no licence". The same
   convention isFeedbackAdmin() follows in services/feedback.js: the quiet
   failure of a permission check has to be the safe direction. */

let state = { active: false, expires_at: null };

export const motionActive = () => state.active;
export const motionExpires = () => state.expires_at;

export async function loadMotionLicence() {
  const user = currentUser();
  /* Anonymous sessions are excluded before the query rather than by it. They
     carry no email claim, so my_motion_licence() would return no rows anyway
     — but a licence is a thing bought by a person, and saying that here is
     clearer than relying on a coalesce in SQL to mean it. */
  if (!isConfigured || !user || isAnonymous()) {
    state = { active: false, expires_at: null };
    emit('motion:changed', state);
    return state;
  }
  const { data, error } = await supabase.rpc('my_motion_licence');
  /* No row is the normal case, not an error — most people do not hold a
     licence. The function returns a set, so that arrives as an empty array
     rather than as null. */
  const row = Array.isArray(data) ? data[0] : data;
  if (error) {
    /* Keep what we had. A network hiccup is not evidence that a licence has
       gone, and clearing on one meant a blip mid-session silently watermarked
       somebody who had paid — the one direction this must not fail in. The
       state can only ever have been set from a server reply, so the worst it
       can be is stale, and the scheduled re-check will correct it. */
    console.warn('[illusio] motion licence check failed', error.message);
    emit('motion:changed', state);
    return state;
  } else {
    state = row
      ? { active: row.active === true, expires_at: row.expires_at ?? null }
      : { active: false, expires_at: null };
  }
  emit('motion:changed', state);
  return state;
}

/** Applied straight from a licence the admin screen just wrote, and used by
 *  tools/verify.mjs to put a session into the licensed state without a
 *  backend — the same shape and the same reason as entitlements.js's
 *  setCreditBalance(). Granting yourself Motion here is not a way in: the
 *  server still owns the row, and every reload asks it again. */
export function setMotionLicence(active, expiresAt = null) {
  state = { active: !!active, expires_at: expiresAt };
  emit('motion:changed', state);
  return state;
}

/* Re-ask the moment a licence is due to lapse.

   `active` is decided by Postgres against now(), which no client clock can
   argue with — but the ANSWER is cached here and was only refreshed on
   auth:changed. A tab left open across the expiry went on believing it, so
   the watermark stayed off and Motion stayed unlocked for as long as the
   session lasted, which for a browser tab is days.

   Same shape services/limits.js already uses for the non-commercial hour:
   one timer, set to the known instant, re-armed after it fires. Capped
   because setTimeout takes a 32-bit delay and a licence with a year on it
   would overflow it to fire immediately, in a loop. */
let lapseTimer = null;
const MAX_DELAY = 2 ** 31 - 1;
function scheduleLapse() {
  clearTimeout(lapseTimer);
  lapseTimer = null;
  if (!state.active || !state.expires_at) return;
  const ms = new Date(state.expires_at) - Date.now();
  if (!Number.isFinite(ms)) return;
  /* A second past the moment, so the server agrees it has gone rather than
     landing exactly on the boundary and coming back still active. */
  lapseTimer = setTimeout(() => { loadMotionLicence(); },
                          Math.min(MAX_DELAY, Math.max(1000, ms + 1000)));
}

on('motion:changed', scheduleLapse);
on('auth:changed', () => { loadMotionLicence(); });

/* Ask again before something irreversible — an export leaving with no mark.

   Only when there is an account the answer could be ABOUT. An anonymous
   session cannot hold a licence, so re-asking tells us nothing we did not
   already know, and it would clear a licence applied locally the moment an
   admin granted one. Signing in and the scheduled lapse cover every other
   way the answer can change; this covers the one they do not, which is an
   admin revoking a licence while the customer has the tab open. */
export async function revalidateMotion() {
  if (!isConfigured || !currentUser() || isAnonymous()) return state;
  return loadMotionLicence();
}
